import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Check, RefreshCw } from 'lucide-react';
import type { Agency } from '@atlas/shared';
import { fetchAgencies, refreshAgencies } from '../lib/agencies.js';

interface AgencyPickerProps {
  value: Agency | null;
  onChange: (agency: Agency | null) => void;
}

const MAX_RESULTS = 50;

/**
 * Searchable dropdown over the active agencies. Fetches the list once (cached),
 * filters client-side by name, and returns the chosen agency (id + name).
 */
export function AgencyPicker({ value, onChange }: AgencyPickerProps) {
  const [agencies, setAgencies] = useState<Agency[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAgencies()
      .then((list) => {
        if (cancelled) return;
        setAgencies(list);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load agencies.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const matches = useMemo(() => {
    if (!agencies) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? agencies.filter((a) => a.name.toLowerCase().includes(q))
      : agencies;
    return list.slice(0, MAX_RESULTS);
  }, [agencies, query]);

  function select(agency: Agency) {
    onChange(agency);
    setQuery('');
    setOpen(false);
  }

  async function retry() {
    setLoading(true);
    setError(null);
    try {
      const list = await refreshAgencies();
      setAgencies(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load agencies.');
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = matches[highlight];
      if (pick) select(pick);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const placeholder = loading
    ? 'Loading agencies…'
    : value
      ? value.name
      : 'Search active agencies…';

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`flex items-center gap-2 px-2.5 py-1.5 border rounded bg-white ${
          open ? 'border-[#2383e2] ring-1 ring-[#2383e2]' : 'border-[#e9e9e7]'
        }`}
      >
        <Search size={14} className="text-[#aeaaa4] shrink-0" />
        <input
          className="flex-1 text-sm text-[#37352f] placeholder:text-[#aeaaa4] focus:outline-none bg-transparent"
          placeholder={placeholder}
          value={query}
          disabled={loading && !agencies}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {value && !query && (
          <Check size={14} className="text-[#0f7b6c] shrink-0" />
        )}
      </div>

      {error && (
        <div className="mt-1 flex items-center gap-2 text-xs text-[#e03e3e]">
          <span>{error}</span>
          <button
            type="button"
            onClick={retry}
            className="inline-flex items-center gap-1 text-[#2383e2] hover:underline"
          >
            <RefreshCw size={11} /> Retry
          </button>
        </div>
      )}

      {open && !error && agencies && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded border border-[#e9e9e7] bg-white shadow-lg">
          {matches.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#aeaaa4]">
              No matching agencies.
            </div>
          ) : (
            matches.map((a, i) => (
              <button
                type="button"
                key={a.id}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => select(a)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm ${
                  i === highlight ? 'bg-[#f1f1ef]' : 'bg-white'
                } ${value?.id === a.id ? 'text-[#0f7b6c]' : 'text-[#37352f]'}`}
              >
                <span className="truncate">{a.name}</span>
                {value?.id === a.id && <Check size={13} className="shrink-0" />}
              </button>
            ))
          )}
          {agencies.length > 0 && (
            <div className="px-3 py-1.5 text-[10px] text-[#aeaaa4] border-t border-[#f1f1ef] sticky bottom-0 bg-white">
              {query.trim()
                ? `${matches.length} shown`
                : `${agencies.length} active agencies`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
